import {useRef, useState} from 'react'

type ValidationRule = (() => boolean) | boolean;
export type ValidationRules = Record<string, ValidationRule>;
export type ValidatedRules<T> = { [K in keyof T]: boolean };

interface AsyncValidator<T> {
    hit: (data: T) => void;
    result: boolean;
    isLoading: boolean;
}

export function useAsyncValidator<T>(millis: number, call: (data: T) => Promise<boolean>): AsyncValidator<T> {
    const [isLoading, setIsLoading] = useState(false);
    const [result, setResult] = useState(false);
    const last = useRef<Date>(new Date())
    const timer = useRef<number>();

    function hit(data: T, fromTimer: boolean) {
        const now = new Date();
        // clear timer
        if (timer.current != null)
            window.clearTimeout(timer.current)

        setIsLoading(true)
        setResult(false)
        
        if (fromTimer && now.valueOf() - last.current.valueOf() > millis) {
            call(data)
                .then(r => setResult(r))
                .finally(() => {setIsLoading(false)})
        } else {
            timer.current = window.setTimeout(() => hit(data,true), millis)
        }
        last.current = now;
    }

    return {
        hit: (data) => hit(data,false),
        isLoading,
        result
    }
}

function runRule(rule: ValidationRule): boolean {
    if (typeof rule === 'boolean') {
        return rule
    }

    return rule()
}


function run<T>(rules: T, state: (rule: ValidationRule) => boolean): { rules: ValidatedRules<T>, passed: boolean } {
    const r: any = {}
    let passed = true
    for (const k in rules) {
        r[k] = state(rules[k] as any)
        if (!r[k]) {
            passed = false
        }
    }

    return {rules: r, passed}
}

export function useValidation<T extends ValidationRules>(rules: T): {
    validate: () => boolean,
    clear: () => void;
    rules: ValidatedRules<T>;
    dirtyPassed: boolean;
} {
    const [realTime, setRealTime] = useState(false)

    function validate(): boolean {
        setRealTime(true)
        return run(rules, runRule).passed
    }

    function clear(): void {
        setRealTime(false)
    }

    const validated = realTime
        ? run(rules, runRule)
        : run(rules, () => true)

    return {
        clear,
        validate,
        rules: validated.rules,
        dirtyPassed: validated.passed
    }
}
